import {
  THREAD_REPLICA_CHECKPOINTS_COMPATIBLE_CURSOR_INDEX,
  THREAD_REPLICA_CHECKPOINTS_STORE,
  THREAD_REPLICA_EVENTS_REPLAY_IDENTITY_INDEX,
  THREAD_REPLICA_EVENTS_STORE,
  THREAD_REPLICA_IDB_NAME,
  THREAD_REPLICA_IDB_VERSION,
  THREAD_REPLICA_META_STORE,
} from "@/lib/thread-replica-types"

/**
 * If an older tab blocks a replica DB upgrade after deploy, skip the local
 * replica rather than hanging session activation behind IndexedDB forever.
 */
const THREAD_REPLICA_DB_OPEN_BLOCKED_TIMEOUT_MS = 2_000

function hasIndexedDb(): boolean {
  return typeof globalThis !== "undefined" && typeof globalThis.indexedDB !== "undefined"
}

export function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
  })
}

export function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"))
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"))
  })
}

let dbPromise: Promise<IDBDatabase | null> | null = null

export async function openThreadReplicaDb(): Promise<IDBDatabase | null> {
  if (!hasIndexedDb()) {
    return null
  }

  if (dbPromise) {
    return dbPromise
  }

  dbPromise = new Promise((resolve) => {
    try {
      let settled = false
      const finish = (db: IDBDatabase | null) => {
        if (settled) return
        settled = true
        clearTimeout(blockedTimeout)
        resolve(db)
      }
      const blockedTimeout = setTimeout(() => {
        dbPromise = null
        finish(null)
      }, THREAD_REPLICA_DB_OPEN_BLOCKED_TIMEOUT_MS)
      const request = globalThis.indexedDB.open(THREAD_REPLICA_IDB_NAME, THREAD_REPLICA_IDB_VERSION)
      request.onupgradeneeded = () => {
        const db = request.result

        if (!db.objectStoreNames.contains(THREAD_REPLICA_META_STORE)) {
          db.createObjectStore(THREAD_REPLICA_META_STORE, {
            keyPath: ["scopeKey", "sessionId"],
          })
        }

        if (!db.objectStoreNames.contains(THREAD_REPLICA_EVENTS_STORE)) {
          const eventsStore = db.createObjectStore(THREAD_REPLICA_EVENTS_STORE, {
            keyPath: ["scopeKey", "sessionId", "lane", "sequence"],
          })
          eventsStore.createIndex(
            THREAD_REPLICA_EVENTS_REPLAY_IDENTITY_INDEX,
            ["scopeKey", "sessionId", "lane", "replayEventId"],
            { unique: true },
          )
        }

        const checkpointsStore = db.objectStoreNames.contains(THREAD_REPLICA_CHECKPOINTS_STORE)
          ? request.transaction?.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)
          : db.createObjectStore(THREAD_REPLICA_CHECKPOINTS_STORE, {
            keyPath: ["scopeKey", "sessionId", "replicaEpoch", "cursor"],
          })
        if (
          checkpointsStore &&
          !checkpointsStore.indexNames.contains(THREAD_REPLICA_CHECKPOINTS_COMPATIBLE_CURSOR_INDEX)
        ) {
          checkpointsStore.createIndex(
            THREAD_REPLICA_CHECKPOINTS_COMPATIBLE_CURSOR_INDEX,
            [
              "scopeKey",
              "sessionId",
              "replicaEpoch",
              "eventContractVersion",
              "projectorVersion",
              "cursor",
            ],
            { unique: false },
          )
        }
      }
      request.onsuccess = () => {
        const db = request.result
        if (settled) {
          db.close()
          return
        }
        db.onversionchange = () => {
          db.close()
          dbPromise = null
        }
        db.onclose = () => {
          dbPromise = null
        }
        finish(db)
      }
      request.onblocked = () => {
        dbPromise = null
        finish(null)
      }
      request.onerror = () => {
        dbPromise = null
        finish(null)
      }
    } catch {
      dbPromise = null
      resolve(null)
    }
  })

  return dbPromise
}

export async function resetThreadReplicaDbForTests(): Promise<void> {
  const db = await dbPromise?.catch(() => null)
  db?.close()
  dbPromise = null

  if (!hasIndexedDb()) {
    return
  }

  await new Promise<void>((resolve) => {
    try {
      const request = globalThis.indexedDB.deleteDatabase(THREAD_REPLICA_IDB_NAME)
      request.onsuccess = () => resolve()
      request.onerror = () => resolve()
      request.onblocked = () => resolve()
    } catch {
      resolve()
    }
  })
}
